/**
 * Instructions card for the Contexto_Gestao (handoff §7, `logic/01.js:232-234`).
 *
 * A single cell, not numbered: the reference ties the step number's visibility to having
 * more than one (`numerado = PASSOS.length > 1`, `logic/01.js:886`), and `gestao` has exactly
 * one.
 */

const INSTRUCTION = {
  title: "Personalize sua lista de presentes",
  description:
    "Adicione o que você quer receber, defina quantas unidades e quais pacotes, e destaque até oito peças para abrirem a vitrine. Cada presente comprado pelos convidados vira crédito no seu nome, para resgatar em loja no tempo que a casa pedir.",
} as const;

/** The house's suggestion lists (`INSTRUCOES.sugestao`): one cell, the house speaking. */
const SUGGESTION_INSTRUCTION = {
  title: "Uma curadoria assinada pela casa",
  description:
    "Estas peças foram reunidas pela Tania Bulhões para quem está montando a primeira mesa. Adicione a lista inteira e ajuste o que quiser depois.",
} as const;

/** The creation's curation, for the host (`INSTRUCOES.curadoria`). */
const CURATION_INSTRUCTION = {
  title: "Escolha as peças que abrem a sua lista",
  description:
    "Marque o que você quer receber. A lista não pode nascer vazia, e tudo o que você escolher agora continua editável depois: dá para acrescentar, tirar e definir os pacotes a qualquer momento.",
} as const;

/**
 * The card's own frame, exported so the tab's skeleton (`tab-loading.tsx`) reserves the same
 * border, radius and padding rather than an approximation that could drift from this one.
 *
 * Both `mt` and `mb` mirror the reference's wrapping `<section>` (`template.html:378`,
 * `padding: clamp(18px,...,24px) 0`, i.e. top **and** bottom): the card sat with no space
 * below it, so the grid heading that follows read as glued to it.
 */
export const CATALOG_INSTRUCTIONS_FRAME_CLASS =
  "mx-auto mt-[clamp(18px,2vw,24px)] mb-[clamp(18px,2vw,24px)] max-w-[1120px] rounded-none border border-[var(--tb-hairline)] bg-tb-white p-[clamp(26px,3.2vw,40px)]";

/**
 * What the guest reads in the host's preview: the prototype's `convidado` context, three numbered
 * steps. Fixed copy, with no host names interpolated.
 */
const GUEST_STEPS = [
  { number: "01", title: "Escolha um presente", description: "Cada peça desta lista foi escolhida pelos anfitriões." },
  { number: "02", title: "Escreva a sua mensagem", description: "Ao comprar o presente, você poderá deixar uma mensagem aos anfitriões." },
  { number: "03", title: "O presente", description: "O casal resgata o presente com a sua consultora, em nossas lojas, no momento mais conveniente para eles." },
] as const;

const TITLE_CLASS = "text-[clamp(19px,1.9vw,22px)] font-normal leading-[1.22] text-[var(--fg)] [text-wrap:pretty]";
const DESCRIPTION_CLASS = "mt-3 text-[14px] leading-[1.65] text-[var(--fg-muted)] [text-wrap:pretty]";

export function CatalogInstructions({
  audience = "gestao",
}: {
  /**
   * Who reads the card: the host managing the list, a guest in the host's preview, or a visitor
   * on one of the house's suggestion lists.
   */
  audience?: "gestao" | "convidado" | "sugestao" | "curadoria";
}) {
  if (audience === "convidado") {
    return (
      // The prototype's numbered layout: up to three columns above 1024px, one below; in each,
      // the number in its own column and the description under the title.
      <div
        className={`${CATALOG_INSTRUCTIONS_FRAME_CLASS} grid gap-[clamp(22px,3.2vw,44px)] [@media(min-width:1025px)]:grid-cols-3`}
      >
        {GUEST_STEPS.map((step) => (
          <div key={step.number} className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] content-start items-start gap-x-3.5">
            <span className="pt-[7px] font-mono text-[12px] leading-none tracking-[0.06em] text-tb-green">
              {step.number}
            </span>
            <span className={TITLE_CLASS}>{step.title}</span>
            <span className={`col-start-2 ${DESCRIPTION_CLASS}`}>{step.description}</span>
          </div>
        ))}
      </div>
    );
  }

  const instruction =
    audience === "sugestao" ? SUGGESTION_INSTRUCTION : audience === "curadoria" ? CURATION_INSTRUCTION : INSTRUCTION;

  return (
    <div className={CATALOG_INSTRUCTIONS_FRAME_CLASS}>
      {/* Sans, as the prototype sets it: the card speaks in the interface's voice, not the display face. */}
      <p className={TITLE_CLASS}>{instruction.title}</p>
      <p className={DESCRIPTION_CLASS}>{instruction.description}</p>
    </div>
  );
}
